import React from "react";
import PropTypes from "prop-types";
import { Button } from "@lululemon/ui-library";
import { leftChevronIcon, rightChevronIcon } from "@lululemon/ui-icons";

import { useStoryNavigation } from "../utils/reel-navigation-context";

import styles from "./reel-navigation-controls.module.scss";

export const ChevronNavigationControls = ({
  children,
  handleButtonContainerClick,
  isVisible,
  storiesLength,
}) => {
  const {
    hasPresentedAllStories,
    handleBackClick,
    handleNextClick,
    storyIndex,
    isStoryLoading,
  } = useStoryNavigation();

  const handleNext = (e) => {
    e.stopPropagation();
    if (isStoryLoading) return;
    handleNextClick(e);
  };

  const handleBack = (e) => {
    e.stopPropagation();
    if (isStoryLoading) return;
    handleBackClick(e);
  };

  return (
    <>
      {isVisible && (
        <div
          className={styles.chevronNavigationButtonContainer}
          onClick={handleButtonContainerClick}
        >
          {storyIndex !== 0 && !hasPresentedAllStories && (
            <Button
              className={styles.chevronNavigationButton}
              data-testid="prev-story"
              icon={leftChevronIcon}
              onClick={handleBack}
            >
              <span className="lll-hidden-visually">
                Navigate to previous slide
              </span>
            </Button>
          )}
        </div>
      )}
      <div className={styles.chevronNavigationContent}>{children}</div>
      {isVisible && (
        <div
          className={styles.chevronNavigationButtonContainer}
          onClick={handleButtonContainerClick}
        >
          {storyIndex < storiesLength - 1 && (
            <Button
              className={styles.chevronNavigationButton}
              data-testid="next-story"
              icon={rightChevronIcon}
              onClick={handleNext}
            >
              <span className="lll-hidden-visually">
                Navigate to next slide
              </span>
            </Button>
          )}
        </div>
      )}
    </>
  );
};

ChevronNavigationControls.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  handleButtonContainerClick: PropTypes.func.isRequired,
  isVisible: PropTypes.bool.isRequired,
  storiesLength: PropTypes.number.isRequired,
};

export const GesturesNavigationControls = ({
  dispatchInstaStoryAction,
  videoRef,
}) => {
  const longPressTimeOut = React.useRef();
  const [isHoldPress, setIsHoldPress] = React.useState(false);
  const { handleBackClick, handleNextClick, isStoryLoading } =
    useStoryNavigation();

  const handleOnMouseDown = (e) => {
    e.stopPropagation();

    longPressTimeOut.current = setTimeout(() => {
      setIsHoldPress(true);
      dispatchInstaStoryAction("pause");
      videoRef.current?.pause();
    }, 200);
  };

  const handleOnMouseUp = (e) => {
    e.stopPropagation();

    if (longPressTimeOut.current) {
      clearTimeout(longPressTimeOut.current);
      longPressTimeOut.current = null;
    }

    dispatchInstaStoryAction("play");
    videoRef.current?.play();
    setIsHoldPress(false);
  };

  const handleBackContainerClick = (e) => {
    if (isStoryLoading) return;

    if (!isHoldPress) {
      handleBackClick(e);
    }
  };

  const handleNextContainerClick = (e) => {
    if (isStoryLoading) return;

    if (!isHoldPress) {
      handleNextClick(e);
    }
  };

  return (
    <div
      className={styles.gesturesContainer}
      data-testid="gesture-navigation-container"
    >
      <Button
        aria-label="Navigate to previous slide"
        className={styles.gesturesContainerBack}
        variant="transparent"
        onTouchStart={handleOnMouseDown}
        onTouchEnd={handleOnMouseUp}
        onMouseDown={handleOnMouseDown}
        onMouseUp={handleOnMouseUp}
        onClick={handleBackContainerClick}
        data-testid="gesture-navigation-back"
      />
      <Button
        aria-label="Navigate to next slide"
        className={styles.gesturesContainerNext}
        variant="transparent"
        onTouchStart={handleOnMouseDown}
        onTouchEnd={handleOnMouseUp}
        onMouseDown={handleOnMouseDown}
        onMouseUp={handleOnMouseUp}
        onClick={handleNextContainerClick}
        data-testid="gesture-navigation-next"
      />
    </div>
  );
};

GesturesNavigationControls.defaultProps = {
  videoRef: null,
};

GesturesNavigationControls.propTypes = {
  dispatchInstaStoryAction: PropTypes.func.isRequired,
  videoRef: PropTypes.object,
};
